/** SUNTRONIX ONE — ONE INBOX. Forward anything. ONE files it where it belongs. */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Inbox, Copy, Upload, RefreshCw, Eye, CheckCircle2, AlertTriangle, ShieldAlert,
  FileText, Mail, X, Send, KeyRound,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { GmailIntakeCard } from "./GmailIntakeCard";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

const TABS = [["ALL", "All"], ["AUTO_FILED", "Auto Filed"], ["NEEDS_REVIEW", "Needs Review"],
  ["FAILED", "Failed"], ["QUARANTINED", "Quarantined"]];
const ATT_TONE = {
  AUTO_FILED: "text-emerald-700 bg-emerald-50 ring-emerald-200",
  NEEDS_REVIEW: "text-amber-700 bg-amber-50 ring-amber-200",
  UNCLASSIFIED: "text-amber-700 bg-amber-50 ring-amber-200",
  PASSWORD_REQUIRED: "text-violet-700 bg-violet-50 ring-violet-200",
  QUARANTINED: "text-rose-700 bg-rose-50 ring-rose-200",
  DUPLICATE: "text-slate-500 bg-slate-50 ring-slate-200",
  FAILED: "text-rose-700 bg-rose-50 ring-rose-200",
};

const copyText = (text, label) => {
  try { navigator.clipboard?.writeText(text)?.then?.(() => {}, () => {}); toast.success(label || "Copied"); }
  catch { toast.success(label || "Copied"); }
};
const PREVIEWABLE = ["AUTO_FILED", "NEEDS_REVIEW", "UNCLASSIFIED", "PASSWORD_REQUIRED", "DUPLICATE"];

const blobPreview = async (attId) => {
  try {
    const { data } = await api.get(`/inbox/attachments/${attId}/preview`, { responseType: "blob" });
    const url = URL.createObjectURL(data); window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) { toast.error(e.response?.data?.detail || "Preview unavailable"); }
};

export default function OneInbox() {
  const nav = useNavigate();
  const [meta, setMeta] = useState(null);
  const [summary, setSummary] = useState(null);
  const [tab, setTab] = useState("ALL");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [simOpen, setSimOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/inbox/messages", { params: tab === "ALL" ? {} : { status: tab } });
      setItems(data.items || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  }, [tab]);
  const loadAux = useCallback(async () => {
    try { const [m, s] = await Promise.all([api.get("/inbox/meta"), api.get("/inbox/summary")]); setMeta(m.data); setSummary(s.data); } catch { /* noop */ }
  }, []);
  useEffect(() => { loadAux(); }, [loadAux]);
  useEffect(() => { load(); }, [load]);

  const addr = meta?.intake_address || "one@suntronix.net";
  const copyAddr = () => copyText(addr, "Address copied");

  const confirmFile = async (att) => {
    try { await api.post(`/inbox/attachments/${att.id}/confirm`, {}); toast.success("Filed to Document Vault"); load(); loadAux(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const quarantine = async (att) => {
    try { await api.post(`/inbox/attachments/${att.id}/quarantine`); toast.success("Quarantined"); load(); loadAux(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="max-w-[1200px] mx-auto p-6 space-y-6" data-testid="one-inbox">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.16em] text-slate-400 flex items-center gap-2"><Inbox className="h-3.5 w-3.5" /> Documents</div>
          <h1 className="text-2xl font-bold text-slate-900">ONE Inbox</h1>
          <p className="text-[12px] text-slate-500 mt-1">Forward anything. ONE files it where it belongs.</p>
        </div>
        <Button className="bg-slate-900 text-white hover:bg-slate-800" onClick={() => setSimOpen(true)} data-testid="inbox-simulate-btn">
          <Upload className="h-4 w-4 mr-1" /> Forward / Upload
        </Button>
      </div>

      <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-5 flex items-center justify-between flex-wrap gap-3" data-testid="inbox-address">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-slate-900 text-white grid place-items-center"><Mail className="h-5 w-5" /></div>
          <div>
            <div className="text-[11px] uppercase tracking-wide text-slate-400">Forward documents to</div>
            <div className="text-lg font-bold text-slate-900 font-mono">{addr}</div>
          </div>
        </div>
        <Button variant="outline" onClick={copyAddr} data-testid="inbox-copy-addr"><Copy className="h-4 w-4 mr-1" /> Copy email address</Button>
      </div>

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="inbox-summary">
          {[["auto_filed_today", "Auto filed today", "text-emerald-700"], ["needs_review", "Needs review", "text-amber-700"],
            ["failed", "Failed", "text-rose-700"], ["quarantined", "Quarantined", "text-rose-700"], ["password_required", "Password required", "text-violet-700"]].map(([k, label, tone]) => (
            <div key={k} className="rounded-lg border border-slate-200 bg-white p-3">
              <div className={`text-2xl font-bold ${tone}`}>{summary[k] ?? 0}</div>
              <div className="text-[11px] text-slate-400">{label}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-1 border-b border-slate-200">
        {TABS.map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`inbox-tab-${id}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === id ? "border-slate-900 text-slate-900" : "border-transparent text-slate-400 hover:text-slate-600"}`}>{label}</button>
        ))}
        <div className="flex-1" />
        <Button variant="ghost" size="icon" onClick={() => { load(); loadAux(); }}><RefreshCw className="h-4 w-4" /></Button>
      </div>

      {loading ? <div className="text-slate-400 text-sm p-6">Loading…</div>
        : items.length === 0 ? <div className="rounded-lg border border-dashed border-slate-300 p-10 text-center text-slate-400" data-testid="inbox-empty">Nothing here yet. Forward an email to {addr} or use “Forward / Upload”.</div>
        : (
          <div className="space-y-3" data-testid="inbox-list">
            {items.map((m) => (
              <div key={m.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid="inbox-message">
                <div className="flex items-center justify-between">
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{m.subject || "(no subject)"}</div>
                    <div className="text-[11px] text-slate-400">from {m.sender}{m.forwarded_by ? ` · fwd ${m.forwarded_by}` : ""} · {String(m.received_at).slice(0, 16).replace("T", " ")}{m.trusted_sender ? " · trusted" : ""}</div>
                  </div>
                  <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full ring-1 ${ATT_TONE[m.status] || "text-slate-500 bg-slate-50 ring-slate-200"}`}>{(m.status || "").replaceAll("_", " ")}</span>
                </div>
                <div className="mt-3 space-y-2">
                  {(m.attachments || []).map((a) => (
                    <div key={a.id} className="flex items-center gap-3 rounded-lg bg-slate-50 border border-slate-100 px-3 py-2" data-testid="inbox-attachment">
                      <FileText className="h-4 w-4 text-slate-400 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-medium text-slate-800 truncate">{a.filename}</div>
                        <div className="text-[11px] text-slate-400 truncate">
                          {a.type ? `${a.type.replaceAll("_", " ")}` : "unclassified"}
                          {a.suggested_destination ? ` → ${a.suggested_destination}` : ""}
                          {typeof a.confidence === "number" ? ` · ${Math.round(a.confidence * 100)}%` : ""}
                          {a.reason ? ` · ${a.reason}` : ""}
                        </div>
                      </div>
                      <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full ring-1 shrink-0 ${ATT_TONE[a.status] || "text-slate-500 bg-slate-50 ring-slate-200"}`} data-testid="inbox-att-status">
                        {a.status === "PASSWORD_REQUIRED" && <KeyRound className="h-3 w-3 inline mr-0.5" />}
                        {a.status === "AUTO_FILED" && <CheckCircle2 className="h-3 w-3 inline mr-0.5" />}
                        {a.status === "QUARANTINED" && <ShieldAlert className="h-3 w-3 inline mr-0.5" />}
                        {(a.status || "").replaceAll("_", " ")}
                      </span>
                      <div className="flex items-center gap-1 shrink-0">
                        {PREVIEWABLE.includes(a.status) && (
                          <Button size="icon" variant="ghost" title="Preview" onClick={() => blobPreview(a.id)} data-testid="inbox-att-preview"><Eye className="h-4 w-4" /></Button>
                        )}
                        {["NEEDS_REVIEW", "UNCLASSIFIED"].includes(a.status) && (
                          <>
                            <Button size="sm" onClick={() => confirmFile(a)} data-testid="inbox-att-confirm">Confirm & file</Button>
                            <Button size="sm" variant="outline" onClick={() => quarantine(a)} data-testid="inbox-att-quarantine">Quarantine</Button>
                          </>
                        )}
                        {a.status === "PASSWORD_REQUIRED" && (
                          <Button size="sm" variant="outline" onClick={() => quarantine(a)} data-testid="inbox-att-quarantine">Quarantine</Button>
                        )}
                        {a.status === "AUTO_FILED" && a.filed_document_id && (
                          <Button size="sm" variant="outline" onClick={() => nav("/vault")} data-testid="inbox-att-openvault">Open in Vault</Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

      {simOpen && <SimulateDialog meta={meta} onClose={() => setSimOpen(false)} onDone={() => { setSimOpen(false); load(); loadAux(); }} />}
      <GoLiveStrip />
      <GmailIntakeCard mailbox="one@suntronix.net" />
      <GmailIntakeCard mailbox="info@suntronix.net" />
    </div>
  );
}

function GoLiveStrip() {
  const [cfg, setCfg] = useState(null);
  const [guide, setGuide] = useState(null);
  const [gp, setGp] = useState("mailgun");
  useEffect(() => { api.get("/inbox/connector").then(({ data }) => setCfg(data)).catch(() => setCfg(false)); }, []);
  const sendDigest = async () => {
    try { const { data } = await api.post("/inbox/digest/send", {}); toast[data.ok ? "success" : "error"](data.ok ? "Daily summary emailed to you" : `Digest ${data.delivery_status}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const openGuide = async () => {
    try { const { data } = await api.get("/inbox/connector/guide"); setGuide(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Guide unavailable"); }
  };
  if (cfg === false) return (
    <div className="pt-2"><Button variant="outline" size="sm" onClick={sendDigest} data-testid="inbox-digest-btn"><Send className="h-4 w-4 mr-1" />Email me today's summary</Button></div>
  );
  if (!cfg) return null;
  const g = guide?.providers?.[gp];
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 mt-2" data-testid="inbox-golive">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[12px] font-semibold text-slate-700">Go live — inbound mailbox connector</div>
          <div className="text-[11px] text-slate-500">{cfg.connected ? `Connected · ${cfg.provider} · last inbound ${cfg.last_inbound_at ? String(cfg.last_inbound_at).slice(0, 16).replace("T", " ") : "—"}` : "Webhook ready — attach a provider to go live"}</div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={openGuide} data-testid="inbox-setup-guide-btn">Setup guide</Button>
          <Button variant="outline" size="sm" onClick={sendDigest} data-testid="inbox-digest-btn"><Send className="h-4 w-4 mr-1" />Email me today's summary</Button>
        </div>
      </div>
      <div className="mt-2 rounded-md bg-white border border-slate-200 p-2 flex items-center gap-2">
        <code className="flex-1 text-[11px] text-slate-600 truncate">{cfg.webhook_url_template}?token=•••</code>
        <Button size="icon" variant="ghost" onClick={() => copyText(cfg.webhook_url_template, "Webhook URL copied")} data-testid="inbox-copy-webhook"><Copy className="h-4 w-4" /></Button>
      </div>
      <div className="text-[10px] text-slate-400 mt-1">Point SendGrid Inbound Parse / Mailgun Routes / Google Workspace routing for {cfg.intake_address} to this URL (POST multipart or JSON). Send the shared secret as header X-Inbox-Token or ?token=.</div>

      {guide && (
        <Dialog open onOpenChange={(o) => !o && setGuide(null)}>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="inbox-guide-dialog">
            <DialogHeader><DialogTitle>Attach an inbound-email provider</DialogTitle>
              <DialogDescription>Forward {guide.intake_address} to the webhook. Pick your provider:</DialogDescription>
            </DialogHeader>
            <div className="flex gap-1">
              {Object.keys(guide.providers).map((k) => (
                <Button key={k} size="sm" variant={gp === k ? "default" : "outline"} onClick={() => setGp(k)} data-testid={`inbox-guide-${k}`}>{guide.providers[k].provider.split(" ")[0]}</Button>
              ))}
            </div>
            {g && (
              <div className="mt-2 space-y-3 text-[12px]">
                <div className="rounded-md bg-slate-50 border border-slate-200 p-2 flex items-center gap-2">
                  <code className="flex-1 text-[11px] text-slate-600 break-all">{g.webhook_url}</code>
                  <Button size="icon" variant="ghost" onClick={() => copyText(g.webhook_url, "Webhook URL copied")}><Copy className="h-4 w-4" /></Button>
                </div>
                <div><div className="font-semibold text-slate-700 mb-1">DNS</div><ul className="list-disc pl-5 text-slate-600 space-y-0.5">{g.dns.map((d, i) => <li key={i}>{d}</li>)}</ul></div>
                <div><div className="font-semibold text-slate-700 mb-1">Steps</div><ol className="list-decimal pl-5 text-slate-600 space-y-0.5">{g.steps.map((s, i) => <li key={i}>{s}</li>)}</ol></div>
                {g.apps_script && (
                  <div>
                    <div className="font-semibold text-slate-700 mb-1 flex items-center justify-between">Apps Script relay
                      <Button size="sm" variant="outline" onClick={() => copyText(g.apps_script, "Script copied")} data-testid="inbox-copy-script"><Copy className="h-3 w-3 mr-1" />Copy</Button>
                    </div>
                    <pre className="bg-slate-900 text-slate-100 text-[10px] rounded-md p-2 overflow-x-auto whitespace-pre-wrap">{g.apps_script}</pre>
                  </div>
                )}
                <div className="text-[11px] text-slate-400">Signed webhooks: {g.signed ? "supported (set MAILGUN_WEBHOOK_SIGNING_KEY for HMAC)" : "uses the shared secret token"}. DNS + provider account are your steps; ping me to verify once done.</div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function SimulateDialog({ meta, onClose, onDone }) {
  const [subject, setSubject] = useState("");
  const [sender, setSender] = useState("");
  const [body, setBody] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  const submit = async () => {
    if (!sender) { toast.error("Sender email required"); return; }
    if (!files.length) { toast.error("Attach at least one file"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("subject", subject); fd.append("sender", sender);
      fd.append("forwarded_by", sender); fd.append("body", body);
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/inbox/ingest", fd);
      if (data.duplicate) toast.info("Duplicate email — already ingested");
      else toast.success("Ingested — ONE is filing it");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="inbox-simulate-dialog">
        <DialogHeader><DialogTitle>Forward to ONE Inbox</DialogTitle>
          <DialogDescription>Simulates an email forwarded to {meta?.intake_address || "one@suntronix.net"}. Optional subject hints: {(meta?.subject_hints || []).join(" ")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input placeholder="From (sender email) *" value={sender} onChange={(e) => setSender(e.target.value)} data-testid="inbox-sim-sender" />
          <Input placeholder="Subject (e.g. [BANK] HSBC statement)" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="inbox-sim-subject" />
          <Textarea rows={2} placeholder="Email body (optional)" value={body} onChange={(e) => setBody(e.target.value)} />
          <div onClick={() => ref.current?.click()} className="rounded-lg border-2 border-dashed border-slate-300 p-4 text-center cursor-pointer" data-testid="inbox-sim-drop">
            <Upload className="h-5 w-5 mx-auto text-slate-400 mb-1" />
            {files.length ? <div className="text-sm text-slate-700">{files.map((f) => f.name).join(", ")}</div> : <div className="text-sm text-slate-500">Attach files</div>}
            <input ref={ref} type="file" multiple className="hidden" onChange={(e) => setFiles(Array.from(e.target.files || []))} data-testid="inbox-sim-files" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-slate-900 text-white hover:bg-slate-800" onClick={submit} disabled={busy} data-testid="inbox-sim-submit"><Send className="h-4 w-4 mr-1" />{busy ? "Sending…" : "Forward to ONE"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
